import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '../../utils/api';
import { FaIdCard, FaMapMarkerAlt, FaArrowRight, FaUser } from 'react-icons/fa';
import MemberEditModal from '../../components/MemberEditModal';

export default function UserHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user?.memberId) {
      API.get(`/members/${user.memberId}`)
        .then(res => setMemberData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>
            {greeting}, {user?.name?.split(' ')[0] || 'Member'}! 👋
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            {now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="page-content">
        {/* Member ID Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
          borderRadius: 16, padding: '24px 28px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: 'white', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', right: -10, top: -20, fontSize: 100, opacity: 0.08 }}>🌿</div>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4 }}>Your Member ID</div>
            <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.05em' }}>
              {user?.memberId || 'KSMP00001'}
            </div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>Sri Karunasai Medicinal Plantation Association</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <img
              src="/logo.png"
              alt="Sri Karunasai Emblem"
              style={{
                width: 68, height: 68,
                borderRadius: '50%',
                objectFit: 'contain',
                background: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: 2
              }}
            />
          </div>
        </div>

        {/* Info Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div className="stat-card green">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaIdCard color="#16a34a" size={18} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Membership Status</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="badge badge-success" style={{ fontSize: 14, padding: '5px 14px' }}>
                ✅ Active Member
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              Member since: {memberData?.created_at?.split('T')[0] || memberData?.created_at?.split(' ')[0] || 'N/A'}
            </div>
          </div>

          <div className="stat-card blue">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, background: '#eff6ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaMapMarkerAlt color="#3b82f6" size={18} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Location</div>
            </div>
            {loading ? <div className="spinner" style={{ width: 20, height: 20 }}></div> : (
              <div>
                <div style={{ fontWeight: 600, color: '#374151' }}>{memberData?.village || '—'}, {memberData?.district || '—'}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{memberData?.taluk || '—'}, {memberData?.pin || '—'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Member Details */}
        {memberData && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FaUser color="#16a34a" />
                <h3 style={{ fontWeight: 700, fontSize: 16 }}>My Details</h3>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => navigate('/user/profile')}>
                View Full Profile <FaArrowRight size={12} />
              </button>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                {[
                  { label: 'Full Name', value: memberData.name },
                  { label: 'Father Name', value: memberData.father_name },
                  { label: 'Mobile', value: memberData.mobile_phone },
                  { label: 'Category', value: memberData.category },
                  { label: 'District', value: memberData.district },
                  { label: 'Aadhaar', value: memberData.aadhaar_no ? '••••' + memberData.aadhaar_no.slice(-4) : '—' },
                ].map((f, i) => f.value && (
                  <div key={i} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{f.label}</div>
                    <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>{f.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div onClick={() => navigate('/user/profile')}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'none'}
            style={{ padding: 20, cursor: 'pointer', transition: 'all 0.2s', borderRadius: 12, background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }}>👤</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>View Full Profile</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>See all your registration details</div>
          </div>
          <div onClick={() => setIsEditing(true)}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'none'}
            style={{ padding: 20, cursor: 'pointer', transition: 'all 0.2s', borderRadius: 12, background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }}>✏️</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Edit Profile</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Update contact, address & info</div>
          </div>
          <div onClick={() => navigate('/user/chat')}
            style={{ padding: 20, cursor: 'pointer', transition: 'all 0.2s', borderRadius: 12, background: 'white', border: '1px solid #e5e7eb' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'none'}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }}>💬</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Chat Support</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Get help from our team</div>
          </div>
        </div>
      </div>

      {/* Member Edit Modal (Aadhaar locked for regular users) */}
      {isEditing && memberData && (
        <MemberEditModal
          member={memberData}
          isAdmin={false}
          onClose={() => setIsEditing(false)}
          onSuccess={(updated) => setMemberData(updated)}
        />
      )}
    </div>
  );
}
